/**
 * DrawerCtaPrimary - Primary CTA button for calculator tooltip drawers
 *
 * Purpose: Solid pink animated button with shimmer effect
 * Matches: "Talk to Our Team" styling from calculator left panel
 *
 * DO NOT use outside calculator drawers - this is drawer-specific styling
 * For site-wide CTAs, use ButtonCta component from /components/ui/ButtonCta.tsx
 */

'use client';

import React from 'react';

interface DrawerCtaPrimaryProps {
  text: string;
  href?: string;
  onClick?: () => void;
  className?: string;
}

export const DrawerCtaPrimary: React.FC<DrawerCtaPrimaryProps> = ({
  text,
  href,
  onClick,
  className = '',
}) => {
  const baseClasses = "w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#ff34a7] text-white rounded-lg font-medium hover:bg-[#e6309b] transition-colors shadow-sm relative overflow-hidden";
  const finalClasses = `${baseClasses} ${className}`.trim();

  // If href provided, render as link
  if (href) {
    return (
      <a
        href={href}
        className={finalClasses}
        style={{ isolation: 'isolate' }}
      >
        <span className="relative z-10">{text}</span>
        <style jsx>{`
          a::after {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            width: 100%;
            height: 100%;
            background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.3), transparent);
            transform: rotate(-45deg) translateZ(0);
            opacity: 0;
            animation: shimmer-premium-drawer 5s infinite;
            pointer-events: none;
            z-index: 1;
            will-change: transform, opacity;
          }

          a:hover::after {
            animation-play-state: paused;
          }

          @keyframes shimmer-premium-drawer {
            0% {
              transform: translateX(-100%) rotate(-45deg);
              opacity: 1;
            }
            100% {
              transform: translateX(100%) rotate(-45deg);
              opacity: 0;
            }
          }
        `}</style>
      </a>
    );
  }

  // Otherwise render as button
  return (
    <button
      type="button"
      onClick={onClick}
      className={finalClasses}
      style={{ isolation: 'isolate' }}
    >
      <span className="relative z-10">{text}</span>
      <style jsx>{`
        button::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.3), transparent);
          transform: rotate(-45deg) translateZ(0);
          opacity: 0;
          animation: shimmer-premium-drawer 5s infinite;
          pointer-events: none;
          z-index: 1;
          will-change: transform, opacity;
        }

        button:hover::after {
          animation-play-state: paused;
        }

        @keyframes shimmer-premium-drawer {
          0% {
            transform: translateX(-100%) rotate(-45deg);
            opacity: 1;
          }
          100% {
            transform: translateX(100%) rotate(-45deg);
            opacity: 0;
          }
        }
      `}</style>
    </button>
  );
};
